import { createElement, forwardRef, type CSSProperties, type ElementType, type HTMLAttributes, type ReactNode } from "react";
import { resolveResponsive, useMatchedBreakpoints, type Responsive } from "./useBreakpoint";
import "./primitives.css";

/** Salt's parseSpacing. A string passes through so token values survive. */
function parseSpacing(v: number | string | undefined): string | undefined {
  if (v === undefined) return undefined;
  return typeof v === "string" ? v : `calc(var(--salt-spacing-100) * ${v})`;
}
/** Salt's parseAlignment: only start/end are translated. */
function parseAlignment(v: string | undefined): string | undefined {
  return v === "start" || v === "end" ? `flex-${v}` : v;
}
/** Salt's parseGridValue: a number becomes an equal-width repeat. */
function parseGridValue(v: number | string | undefined): string | undefined {
  if (v === undefined) return undefined;
  return typeof v === "string" ? v : `repeat(${v}, 1fr)`;
}

type Spacing = Responsive<number | string>;

interface CommonProps extends Omit<HTMLAttributes<HTMLElement>, "style" | "className"> {
  as?: ElementType;
  children?: ReactNode;
  className?: string;
  style?: CSSProperties;
  gap?: Spacing;
  padding?: Spacing;
  margin?: Spacing;
}
interface FlexProps extends CommonProps {
  align?: Responsive<string>;
  justify?: Responsive<string>;
  direction?: Responsive<"row" | "column" | "row-reverse" | "column-reverse">;
  wrap?: Responsive<boolean>;
  separators?: boolean | string;
}
interface GridProps extends CommonProps {
  columns?: Responsive<number | string>;
  rows?: Responsive<number | string>;
  columnGap?: Spacing;
  rowGap?: Spacing;
}

/** Caller style first so the computed layout wins, matching how Salt's class rule behaved. */
function compose(style: CSSProperties | undefined, computed: CSSProperties): CSSProperties {
  return { ...style, ...computed };
}
function join(base: string, className: string | undefined): string {
  return className ? `${base} ${className}` : base;
}

function useFlexVars(p: FlexProps, fallbackDirection: "row" | "column"): CSSProperties {
  const m = useMatchedBreakpoints();
  /*
   * `padding = 0` and `margin = 0` are Salt's defaults and they are LOAD-BEARING, not tidiness.
   * Salt always emits `--flexLayout-padding`, so `padding: var(--flexLayout-padding)` always
   * resolves and every layout element is forced to zero unless the caller says otherwise.
   *
   * Leaving them undefined instead makes the declaration invalid, which lets the app's own CSS
   * padding through. MEASURED: two ancestor stacks on the proposal page each gained 36px of
   * horizontal padding, and 4 x 36 = the exact 144px of content width that went missing
   * (1394px -> 1250px), clipping currency figures mid-number.
   */
  const { align, justify, direction, wrap, gap = 3, padding = 0, margin = 0 } = p;
  return {
    "--flexLayout-gap": parseSpacing(resolveResponsive(gap, m)),
    "--flexLayout-direction": resolveResponsive(direction, m) ?? fallbackDirection,
    "--flexLayout-wrap": resolveResponsive(wrap, m) ? "wrap" : "nowrap",
    "--flexLayout-justify": parseAlignment(resolveResponsive(justify, m)) ?? "flex-start",
    "--flexLayout-align": parseAlignment(resolveResponsive(align, m)) ?? "stretch",
    "--flexLayout-padding": parseSpacing(resolveResponsive(padding, m)),
    "--flexLayout-margin": parseSpacing(resolveResponsive(margin, m)),
  } as unknown as CSSProperties;
}

export const FlexLayout = forwardRef<HTMLElement, FlexProps>(function FlexLayout(
  { as = "div", children, className, style, ...rest },
  ref,
) {
  const vars = useFlexVars(rest as FlexProps, "row");
  const { align, justify, direction, wrap, gap, padding, margin, separators, ...dom } = rest as FlexProps;
  void align, justify, direction, wrap, gap, padding, margin, separators;
  return createElement(as, { ...dom, ref, className: join("saltFlexLayout ds-flex", className), style: compose(style, vars) }, children);
});

export const StackLayout = forwardRef<HTMLElement, FlexProps>(function StackLayout(
  { as = "div", children, className, style, ...rest },
  ref,
) {
  const flex = useFlexVars(rest as FlexProps, "column");
  // Salt's StackLayout wrote --stackLayout-gap too; portal-chrome asserts it directly.
  const vars = { ...flex, "--stackLayout-gap": (flex as Record<string, unknown>)["--flexLayout-gap"] } as CSSProperties;
  const { align, justify, direction, wrap, gap, padding, margin, separators, ...dom } = rest as FlexProps;
  void align, justify, direction, wrap, gap, padding, margin, separators;
  return createElement(as, { ...dom, ref, className: join("saltFlexLayout saltStackLayout ds-stack", className), style: compose(style, vars) }, children);
});

export const FlowLayout = forwardRef<HTMLElement, FlexProps>(function FlowLayout(props, ref) {
  return createElement(FlexLayout, { ...props, direction: "row", wrap: true, ref });
});

export const GridLayout = forwardRef<HTMLElement, GridProps>(function GridLayout(
  { as = "div", children, className, style, ...rest },
  ref,
) {
  const m = useMatchedBreakpoints();
  // Salt's defaults, all four of them. `rows = 1` matters as much as the spacing ones: without
  // it `grid-template-rows` is invalid and the track sizing falls to `auto`.
  const {
    columns = 12,
    rows = 1,
    gap = 3,
    columnGap,
    rowGap,
    padding = 0,
    margin = 0,
    ...dom
  } = rest as GridProps;
  const g = parseSpacing(resolveResponsive(gap, m));
  const vars = {
    "--gridLayout-columns": parseGridValue(resolveResponsive(columns, m)),
    "--gridLayout-rows": parseGridValue(resolveResponsive(rows, m)),
    "--gridLayout-columnGap": parseSpacing(resolveResponsive(columnGap, m)) ?? g,
    "--gridLayout-rowGap": parseSpacing(resolveResponsive(rowGap, m)) ?? g,
    "--gridLayout-padding": parseSpacing(resolveResponsive(padding, m)),
    "--gridLayout-margin": parseSpacing(resolveResponsive(margin, m)),
  } as unknown as CSSProperties;
  return createElement(as, { ...dom, ref, className: join("saltGridLayout ds-grid", className), style: compose(style, vars) }, children);
});
